import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE, getAuthHeaders } from '../utils/api';
import { useUsersStore } from '../store/useUsersStore';
import { formatPakistanDate } from '../utils/dateUtils';

// Custom Filter Component matching Figma design
const FilterDropdown = ({ label, value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : 'Select here';

  return (
    <div className="relative inline-flex" ref={dropdownRef}>
      {/* Container with light-blue border wrapping both boxes */}
      <div className="flex items-center rounded-lg overflow-hidden">
        {/* Blue Label Box - No individual border, no rounding */}
        <div className="bg-primary-blue border-2 border-light-blue  rounded-lg text-white px-5 py-2.5 font-medium text-sm">
          {label}
        </div>
        
        {/* White Dropdown Box - No individual border, no rounding */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-3 px-4 py-2.5 bg-white text-gray-600 hover:bg-gray-50 transition-colors min-w-[160px]"
        >
          <span className="flex-1 text-left text-sm">{displayText}</span>
          <img 
            src={isOpen ? '/Icons/dropup-icon.svg' : '/Icons/dropdown-icon.svg'} 
            alt="dropdown" 
            className="w-6 h-6"
          />
        </button>
      </div>
      
      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[200px] z-50 max-h-60 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 transition-colors ${
                value === option.value ? 'bg-blue-50 text-primary-blue font-medium' : 'text-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


const SuccessBidsPage = () => {
  const navigate = useNavigate();
  const [savedBids, setSavedBids] = useState([]);
  const [loadingBids, setLoadingBids] = useState(true);
  const [error, setError] = useState(null);

  // pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(null);
  const [paginationIsNext, setPaginationIsNext] = useState(false);

  // UI state
  const [viewMode, setViewMode] = useState('list');
  const [bidderFilter, setBidderFilter] = useState('ALL');
  const [bidderTypeFilter, setBidderTypeFilter] = useState('ALL');
  const [projectTypeFilter, setProjectTypeFilter] = useState('ALL');
  const [dateFilter, setDateFilter] = useState('ALL');

  // dynamic sub-users from store
  const subUsers = useUsersStore((s) => s.users || []);

  const userFilterList = useMemo(() => {
    return Array.isArray(subUsers)
      ? subUsers
        .map((u) => {
          const id = String(u.user_bid_id || u.bidder_id || u.user_bid || '');
          const name = u.sub_username || u.name || `sub_${(u.document_id || '').slice(0, 6)}`;
          return id ? { id, name } : null;
        })
        .filter(Boolean)
      : [];
  }, [subUsers]);

  const userMapping = useMemo(() => {
    return (Array.isArray(subUsers) ? subUsers : []).reduce((acc, u) => {
      const id = String(u.user_bid_id || u.bidder_id || u.user_bid || '');
      if (id) acc[id] = u.sub_username || u.name || acc[id] || `sub_${(u.document_id || '').slice(0, 6) || id}`;
      return acc;
    }, {});
  }, [subUsers]);

  // Filter Options
  const bidderOptions = [
    { value: 'ALL', label: 'All Bidders' },
    ...userFilterList.map(u => ({ value: u.id, label: u.name }))
  ];

  const bidTypeOptions = [
    { value: 'ALL', label: 'All Types' },
    { value: 'manual', label: 'Manual' },
    { value: 'auto', label: 'Auto' }
  ];

  const projectTypeOptions = [
    { value: 'ALL', label: 'All Types' },
    { value: 'fixed', label: 'Fixed' },
    { value: 'hourly', label: 'Hourly' }
  ];

  const dateOptions = [
    { value: 'ALL', label: 'All Time' },
    { value: '24 hours', label: 'Last 24 hours' },
    { value: '3 days', label: 'Last 3 days' },
    { value: '7 days', label: 'Last 7 days' },
    { value: '14 days', label: 'Last 14 days' }
  ];

  const badgeClass = {
    bidder: 'bg-blue-100 text-blue-800 border-blue-300',
    period: 'bg-orange-100 text-orange-800 border-orange-300',
    bidType: 'bg-purple-100 text-purple-800 border-purple-300',
    projectType: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    budget: {
      low: 'bg-green-100 text-green-800 border-green-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      high: 'bg-red-100 text-red-800 border-red-300',
    },
  };

  const getBudgetBadgeClass = (min, max) => {
    const amount = max || min || 0;
    if (amount <= 50) return badgeClass.budget.low;
    if (amount <= 500) return badgeClass.budget.medium;
    return badgeClass.budget.high;
  };

  const getBidAmountBadgeClass = (amount) => {
    if (amount == null) return 'bg-gray-100 text-gray-800 border-gray-300';
    if (amount <= 50) return 'bg-green-100 text-green-800 border-green-300';
    if (amount > 100) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-yellow-300';
  };

  const buildBidsUrl = useCallback(() => {
    const params = [];
    if (bidderFilter && bidderFilter !== 'ALL') params.push(`bidder_id=${encodeURIComponent(bidderFilter)}`);
    if (bidderTypeFilter && bidderTypeFilter !== 'ALL') params.push(`bidder_type=${encodeURIComponent(bidderTypeFilter)}`);
    if (projectTypeFilter && projectTypeFilter !== 'ALL') params.push(`type=${encodeURIComponent(projectTypeFilter)}`);
    if (dateFilter && dateFilter !== 'ALL') params.push(`date_from=${encodeURIComponent(dateFilter)}`);
    params.push(`page=${encodeURIComponent(page)}`);
    params.push(`offset=${encodeURIComponent(limit)}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return `${(API_BASE || '').replace(/\/$/, '')}/bids${qs}`;
  }, [bidderFilter, bidderTypeFilter, projectTypeFilter, dateFilter, page, limit]);

  const loadBids = useCallback(async () => {
    try {
      setLoadingBids(true);
      setError(null);
      const url = buildBidsUrl();
      const headers = getAuthHeaders();
      const res = await axios.get(url, { headers, validateStatus: () => true });
      if (!(res.status >= 200 && res.status < 300)) {
        throw new Error(res?.data?.message || `Failed to load bids: ${res.status}`);
      }
      const bids = Array.isArray(res.data?.data) ? res.data.data : [];
      const pagination = res.data?.pagination || res.data?.meta || res.data?.paging || null;
      if (pagination) {
        const respPage = Number(pagination.page) || page;
        const respLimit = Number(pagination.limit || pagination.offset) || limit;
        const respIsNext = !!pagination.is_next;
        setPage(respPage);
        setLimit(respLimit);
        setPaginationIsNext(respIsNext);
        const possibleTotal = pagination.total ?? pagination.total_count ?? pagination.totalCount ?? pagination.count;
        const numericTotal = typeof possibleTotal !== 'undefined' && possibleTotal !== null ? Number(possibleTotal) : null;
        setTotalCount(Number.isFinite(numericTotal) ? numericTotal : null);
      } else {
        setPaginationIsNext(false);
        setTotalCount(Array.isArray(bids) ? bids.length : null);
      }

      setSavedBids(bids);
    } catch (err) {
      console.error('Failed to load bids:', err);
      setSavedBids([]);
      setError(err?.message || 'Failed to load bids');
    } finally {
      setLoadingBids(false);
    }
  }, [buildBidsUrl, page, limit]);

  const canPrev = page > 1;
  const canNext = paginationIsNext || (savedBids.length === limit && !(totalCount !== null && page * limit >= totalCount));
  const goPrev = () => { if (canPrev) setPage((p) => p - 1); };
  const goNext = () => { if (canNext) setPage((p) => p + 1); };
  const changeLimit = (newLimit) => { setLimit(Number(newLimit)); setPage(1); };

  useEffect(() => {
    loadBids();
  }, [loadBids]);

  const getBidDate = (bid) => {
    try {
      return formatPakistanDate(bid.date || bid.createdAt || bid.created_at) || '';
    } catch {
      return new Date(bid.date || bid.createdAt || Date.now()).toLocaleString();
    }
  };

  const getBidderName = (id) => {
    if (userMapping[String(id)]) return userMapping[String(id)];
    const found = subUsers.find((u) => String(u.user_bid_id || u.bidder_id || '') === String(id));
    if (found) return found.sub_username || found.name || String(id);
    return 'No data found';
  };

  if (loadingBids) return <div className="flex items-center justify-center p-8">Loading bids...</div>;
  if (error) return <div className="p-6 text-red-700">Error: {error}</div>;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with Back Arrow */}
        <div className="flex w-full mb-6 justify-between items-center">
          <div className='flex items-center gap-3'>
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center justify-center"
            >
              <img src="/Icons/arrow-back.svg" alt="Back" className="w-8 h-8" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Bids</h1>
            </div>
          </div>
    
          {/* View Mode Toggle with Icons */}
          <div className="flex items-center gap-2">
            <button 
                onClick={loadBids} 
                aria-label="Refresh"
              >
                <img 
                  src="/Icons/refresh-button.svg" 
                  alt="Refresh" 
                  className="w-10 h-10"
                />
              </button>

            <button onClick={() => setViewMode('list')}>
              <img 
                src={viewMode === 'list' ? '/Icons/list-blue.svg' : '/Icons/list-white.svg'} 
                alt="List view" 
                className="w-10 h-10"
              />
            </button>
            <button onClick={() => setViewMode('grid')}>
              <img 
                src={viewMode === 'grid' ? '/Icons/grid-blue.svg' : '/Icons/grid-white.svg'} 
                alt="Grid view" 
                className="w-10 h-10"
              />
            </button>
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <FilterDropdown
            label="All Bidders"
            value={bidderFilter}
            onChange={setBidderFilter}
            options={bidderOptions}
          />

          <FilterDropdown
            label="Bid Types"
            value={bidderTypeFilter}
            onChange={setBidderTypeFilter}
            options={bidTypeOptions}
          />

          <FilterDropdown
            label="Project Types"
            value={projectTypeFilter}
            onChange={setProjectTypeFilter}
            options={projectTypeOptions}
          />

          <FilterDropdown
            label="Date Filter"
            value={dateFilter}
            onChange={setDateFilter}
            options={dateOptions}
          />
        </div>

        {savedBids.length === 0 ? (
          <div className="bg-white p-6 rounded shadow">No saved bids found.</div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {savedBids.map((bid) => {
              const key = bid.document_id || `${bid.project_id}_${bid.bidder_id}`;
              return (
              <div 
                key={key} 
                className="bg-white p-4 rounded-xl shadow flex flex-col cursor-pointer hover:shadow-lg border-2 border-light-blue"
                onClick={() => navigate('/bids')}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold">{bid.projectTitle}</h3>
                    {/* <div className="text-xs text-gray-500 mt-1">{(bid.type || '').toUpperCase()}</div> */}
                  </div>
                  {/* <div className={`px-3 py-1.5 rounded-lg font-semibold border inline-block ${getBidAmountBadgeClass(bid.amount)}`}>
                    ${bid.amount ?? 'N/A'}
                  </div> */}
                </div>

                <p className="text-sm text-gray-600 mt-2 line-clamp-2">{bid.projectDescription}</p>

                {/* <div className="mt-2 text-sm text-gray-700">
                  <div className="text-xs text-gray-500">Proposal</div>
                  <div className="mt-1 text-sm text-gray-600 line-clamp-3">
                    {bid.description && bid.description.trim() !== '' ? bid.description : <span className="text-gray-400">No proposal provided</span>}
                  </div>
                </div> */}

                     
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-600 opacity-40">
                
                {/* Project Type */}
                <div className="flex items-center gap-1">
                  <span className="text-xs">
                    Type: {(bid.type || 'N/A').toUpperCase()}
                  </span>
                </div>

                {/* Bid Type */}
                <div className="flex items-center gap-1">
                  <span className="text-xs">
                    Bid Type: {bid.bidder_type || 'N/A'}
                  </span>
                </div>

                {/* Bid Period */}
                <div className="flex items-center gap-1">
                  <span className="text-xs">
                    Bid Period: {bid.period ?? 'N/A'} days
                  </span>
                </div>

                {/* Budget */}
                <div className="flex items-center gap-1">
                  <span className="text-xs">
                    Budget: {bid.budget?.minimum != null ? `$${bid.budget.minimum}` : '-'}
                    {' '}–{' '}
                    {bid.budget?.maximum != null ? `$${bid.budget.maximum}` : '-'}
                  </span>
                </div>

              </div>

              {/* Icons row */}
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm opacity-40">

                {/* Bidder */}
                <div className="flex items-center gap-1.5">
                  <img src="/Icons/bid-person-icon.svg" alt="" className="w-4 h-4" />
                  <span className="text-gray-700">
                    {getBidderName(bid.bidder_id)}
                  </span>
                </div>

                {/* Bid Amount */}
                <div className="flex items-center gap-1.5">
                  <img src="/Icons/dollar-bid-icon.svg" alt="" className="w-4 h-4" />
                  <span className="text-gray-700">
                    ${bid.amount ?? 'N/A'}
                  </span>
                </div>

                {/* Date */}
                <div className="flex items-center gap-1.5">
                   <img src="/Icons/clander-icon.svg" alt="" className="w-4 h-4" />
                  <span className="text-gray-700">
                    {getBidDate(bid) ?? 'N/A'}
                  </span>
                </div>

              </div>


                <div className="mt-3 flex-1">
                  <div className="text-sm text-primary-black-shade-10 mb-2 font-semibold">Scores</div>
                  <div className="grid grid-cols-2 gap-2 text-sm bg-light-blue-shade-20 p-2 rounded-lg">
                    {bid.scores ? Object.entries(bid.scores).slice(0, 8).map(([k, v]) => {
                      const formatScore = (val) => {
                        if (val == null) return '-';
                        if (typeof val !== 'number') return String(val);
                        return Number.isInteger(val) ? String(val) : val.toFixed(2);
                      };
                      return (
                        <div key={k} className="flex justify-between bg-white px-2 py-1 rounded">
                          <span className="text-xs text-primary-black-shade-10 opacity-20">{k.replace(/_/g, ' ')}</span>
                          <span className="font-medium text-xs text-primary-black-shade-10 opacity-80 ">{formatScore(v)}</span>
                        </div>
                      );
                    }) : <div className="text-xs text-gray-400">No scores</div>}
                  </div>
                </div>

                <div className="mt-3 flex justify-center items-center gap-2 w-44 mx-auto">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://www.freelancer.com/projects/${bid.url}`, '_blank');
                    }} 
                    className="flex-1 px-2 py-1 bg-white text-primary-blue rounded hover:bg-primary-blue hover:text-white transition-colors border border-primary-blue "
                  >
                    View Project
                  </button>
                </div>
         </div>
            );
            })}
          </div>
        ) : (
         <div className="bg-white rounded shadow overflow-auto border-primary-black-shade-10 border ">
            <table className="min-w-full divide-y divide-primary-blue">
              <thead className="bg-primary-blue">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-white">Project</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-white">Bidder</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-white">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-white">Bid Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-white">Amount</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-white">Period</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-white">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-white">Link</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-primary-black-shade-10">
                {savedBids.map((bid) => (
                  <tr key={bid.document_id || `${bid.project_id}_${bid.bidder_id}`}>
                    <td className="px-4 py-3 text-sm">{bid.projectTitle}</td>
                    <td className="px-4 py-3 text-sm">{getBidderName(bid.bidder_id)}</td>
                    <td className="px-4 py-3 text-sm">{bid.type}</td>
                    <td className="px-4 py-3 text-sm">{bid.bidder_type}</td>
                    <td className="px-4 py-3 text-sm">${bid.amount ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-sm">{bid.period ?? 'N/A'}d</td>
                    <td className="px-4 py-3 text-sm">{getBidDate(bid)}</td>
                    <td className="px-4 py-3 text-sm">
                      <button onClick={() => window.open(`https://www.freelancer.com/projects/${bid.url}`, '_blank', 'noopener,noreferrer')} className="px-2.5 py-1 bg-white border-2  border-primary-blue text-primary-blue rounded-lg text-xs hover:bg-primary-blue hover:text-white transition">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination controls */}
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-primary-blue">
            {(() => {
              const start = savedBids.length === 0 ? 0 : ((page - 1) * limit) + 1;
              const end = savedBids.length === 0 ? 0 : ((page - 1) * limit) + savedBids.length;
              if (totalCount !== null) {
                return `Showing ${start} - ${end} of ${totalCount}`;
              }
              return `Showing ${start} - ${end}${paginationIsNext ? ' (more available)' : ''}`;
            })()}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={goPrev} disabled={!canPrev} className={`px-3 py-1 rounded ${canPrev ? 'bg-white border-2 border-primary-blue text-primary-blue' : 'bg-gray-100 text-primary-blue'}`}>Prev</button>
            <span className=" text-primary-blue rounded border-2 border-primary-blue px-3 py-1">Page {page}</span>
            <button onClick={goNext} disabled={!canNext} className={`px-3 py-1 rounded ${canNext ? 'bg-white border-2 border-primary-blue text-primary-blue' : 'bg-gray-100 text-primary-blue'}`}>Next</button>
            <select value={limit} onChange={(e) => changeLimit(e.target.value)} className="border-2 border-primary-blue px-2 py-1 rounded text-primary-blue">
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
            </select>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SuccessBidsPage;
